const MOBILE_PHONE_PREFIX = "010";
const MOBILE_PHONE_HEAD_LENGTH = 3;
const MOBILE_PHONE_MIDDLE_LENGTH = 4;
const MOBILE_PHONE_MAX_DIGITS = 11;

const NAME_INPUT_ALLOWED_REGEX = /[^가-힣ㄱ-ㅎㅏ-ㅣA-Za-z\s]/g;
const NAME_INVALID_CHAR_REGEX = /[^가-힣A-Za-z\s]/;
const NAME_JAMO_REGEX = /[ㄱ-ㅎㅏ-ㅣ]/;
const NAME_KOREAN_REGEX = /[가-힣]/;
const NAME_ENGLISH_REGEX = /[A-Za-z]/;
const DIGIT_REGEX = /\d/;

export const CONTACT_NAME_MAX_LENGTH = 20;

export const CONTACT_VALIDATION_ERROR_MESSAGES = {
  name: "올바른 이름을 입력해주세요.",
  phone: "올바른 전화번호를 입력해주세요.",
} as const;

export const sanitizePhoneDigits = (value: string): string =>
  (value || "").replace(/\D/g, "");

export const formatMobilePhone = (value: string): string => {
  const digits = sanitizePhoneDigits(value).slice(0, MOBILE_PHONE_MAX_DIGITS);

  if (digits.length <= MOBILE_PHONE_HEAD_LENGTH) {
    return digits;
  }

  if (digits.length <= MOBILE_PHONE_HEAD_LENGTH + MOBILE_PHONE_MIDDLE_LENGTH) {
    return `${digits.slice(0, MOBILE_PHONE_HEAD_LENGTH)}-${digits.slice(
      MOBILE_PHONE_HEAD_LENGTH
    )}`;
  }

  return `${digits.slice(0, MOBILE_PHONE_HEAD_LENGTH)}-${digits.slice(
    MOBILE_PHONE_HEAD_LENGTH,
    MOBILE_PHONE_HEAD_LENGTH + MOBILE_PHONE_MIDDLE_LENGTH
  )}-${digits.slice(
    MOBILE_PHONE_HEAD_LENGTH + MOBILE_PHONE_MIDDLE_LENGTH,
    MOBILE_PHONE_MAX_DIGITS
  )}`;
};

const repeatedPhoneSet = new Set(
  Array.from(
    { length: 10 },
    (_, index) =>
      `${MOBILE_PHONE_PREFIX}${String(index).repeat(
        MOBILE_PHONE_MAX_DIGITS - MOBILE_PHONE_HEAD_LENGTH
      )}`
  )
);

const extraFakePhoneSet = new Set<string>();

export const isObviouslyFakePhone = (value: string): boolean => {
  const digits = sanitizePhoneDigits(value);

  if (digits.length !== MOBILE_PHONE_MAX_DIGITS) {
    return false;
  }

  return repeatedPhoneSet.has(digits) || extraFakePhoneSet.has(digits);
};

export const isValidMobilePhone = (value: string): boolean => {
  const digits = sanitizePhoneDigits(value);
  const isBasicRuleSatisfied =
    digits.length === MOBILE_PHONE_MAX_DIGITS &&
    digits.startsWith(MOBILE_PHONE_PREFIX);

  if (!isBasicRuleSatisfied) {
    return false;
  }

  return !isObviouslyFakePhone(value);
};

export const sanitizeContactNameInput = (value: string): string =>
  (value || "")
    .replace(NAME_INPUT_ALLOWED_REGEX, "")
    .slice(0, CONTACT_NAME_MAX_LENGTH);

export const validateContactName = (
  value: string
): string | undefined => {
  const trimmedName = (value || "").trim();

  if (!trimmedName) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  if (trimmedName.length > CONTACT_NAME_MAX_LENGTH) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  if (NAME_JAMO_REGEX.test(trimmedName)) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  if (NAME_INVALID_CHAR_REGEX.test(trimmedName)) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  const hasKorean = NAME_KOREAN_REGEX.test(trimmedName);
  const hasEnglish = NAME_ENGLISH_REGEX.test(trimmedName);

  if (hasKorean && hasEnglish) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  if (!hasKorean && !hasEnglish) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.name;
  }

  return undefined;
};

export const validateContactPhone = (
  value: string
): string | undefined => {
  const trimmedPhone = (value || "").trim();

  if (!isValidMobilePhone(trimmedPhone)) {
    return CONTACT_VALIDATION_ERROR_MESSAGES.phone;
  }

  return undefined;
};

export const getMobilePhoneCaretPosition = (
  value: string,
  selectionStart: number | null
): number => {
  const rawValue = value || "";
  const safeSelectionStart = Math.max(
    0,
    Math.min(selectionStart ?? rawValue.length, rawValue.length)
  );
  const digitsBeforeCaret = sanitizePhoneDigits(
    rawValue.slice(0, safeSelectionStart)
  ).length;
  const formattedValue = formatMobilePhone(rawValue);
  const totalDigits = sanitizePhoneDigits(formattedValue).length;
  const targetDigitsBeforeCaret = Math.min(digitsBeforeCaret, totalDigits);

  if (targetDigitsBeforeCaret <= 0) {
    return 0;
  }

  let seenDigits = 0;
  for (let index = 0; index < formattedValue.length; index += 1) {
    if (DIGIT_REGEX.test(formattedValue[index])) {
      seenDigits += 1;
    }

    if (seenDigits === targetDigitsBeforeCaret) {
      return index + 1;
    }
  }

  return formattedValue.length;
};
